'use client'

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
	type SetStateAction
} from 'react'

import { SECTION_THEME_ENUM } from '@/_types/base'
import { saveGa4LastAmbulanceSlug } from '@/_utils/analytics/ga4-ambulance-slug.storage'

import { ORDER_FLOW_STEPS, type OrderFlowStep } from '../_constants/order-flow-steps.const'
import { CartProductType, OrderFlowMode, type CartProduct } from '../_types/order-flow.types'

import type { IAmbulance } from '@/_types'
import type { AMBULANCE_PRICE_LIST_TABLE_TYPE } from '@/_types/ambulance'

export type { CartProduct }
export { CartProductType, OrderFlowMode }

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

/**
 * Live template (timeslot) pricing while the user fills patient details — keeps
 * cart sidebar / mobile summary in sync with poisťovňa + ročný program rules.
 */
/** Optional flags for {@link OrderFlowContextValue.goToStep}. */
export type GoToStepOptions = {
	/** Merge step into the current history entry instead of `history.go` (guard redirects, shallow stack). */
	history?: 'replace'
}

/** Payload when the user confirms step 1 — keeps category tab + tint for later steps and back navigation. */
export type AmbulanceSelectionPayload = {
	ambulance: IAmbulance
	topLevelCategoryId: string | number
	sectionTheme: SECTION_THEME_ENUM | null
}

export type TemplateCartPricingSnapshot = {
	timeslotId: number
	/**
	 * Price after insurer row lookup, before annual-program discount adjustments.
	 * Used as the "before discount" figure when an annual program reduces the final price.
	 */
	referencePrice: number
	/** Actual amount to pay after all discount rules have been applied. */
	finalPrice: number
	/**
	 * Row label shown in the cart sidebar and mobile header.
	 * - "Cena" for self-payer or when no insurance is selected.
	 * - "Manažment návštevy" for any insured patient (all insurers except SA00).
	 */
	priceLabel: string
	/**
	 * When true, the cart/header prepends "od" (= "from") before the formatted price.
	 * Only true for DEFAULT/PLUS_WITH types before the patient selects an insurer,
	 * showing the cheapest available price across all insurance companies.
	 */
	showFromPrefix: boolean
}

export type OrderFlowContextValue = {
	/** Which flow variant is active (standard 3-step vs. direct pre-filled). */
	flowMode: OrderFlowMode
	/** Slug of the ambulance chosen in step 1, or `null` when none selected yet. */
	selectedAmbulanceSlug: string | null
	/** Directly overwrite the selected ambulance slug (used for URL-preset init). */
	setSelectedAmbulanceSlug: (slug: string | null) => void
	/** Top-level ambulance category tab id from the last step-1 confirm (restore tab when going back). */
	orderFlowTopLevelCategoryId: string | number | null
	/** Section tint from that category — product cards (step 2) and cart (step 3). */
	orderFlowSectionTheme: SECTION_THEME_ENUM | null
	/**
	 * Price-list table variant for the currently selected ambulance.
	 * Drives which rows are shown on step-2 product cards and how prices are displayed in step 3.
	 * `null` until an ambulance is confirmed in step 1.
	 */
	ambulancePriceListTableType: AMBULANCE_PRICE_LIST_TABLE_TYPE | null
	/** The step the user is currently viewing. */
	currentStep: OrderFlowStep
	/** Highest step the user has ever reached — governs which progress-bar items are clickable. */
	maxStepReached: OrderFlowStep
	/** Products the user added to the cart in step 2. */
	cartProducts: CartProduct[]
	/**
	 * Navigate programmatically to a step.
	 * Silently no-ops if the target step is not yet reachable
	 * (e.g. step 2 without an ambulance, step 3 without a cart).
	 *
	 * @remarks Standard flow: each forward confirm **pushes** a `history.state` entry (`orderStep` +
	 * `orderVisitedMax`); the progress bar uses `history.go`. The visible URL is unchanged. After a full
	 * reload, the provider resets the funnel to step 1, merges that into `history.state`, and may
	 * `history.go(-n)` to drop stacked funnel `pushState` entries so one OS back leaves order; stale
	 * same-tab entries are ignored until the user confirms ambulance again.
	 */
	goToStep: (step: OrderFlowStep, options?: GoToStepOptions) => void
	/**
	 * Called when the user confirms their ambulance choice in step 1.
	 * Always clears the cart so step 3 is locked until a new product is picked.
	 * Advances to step 2.
	 */
	confirmAmbulanceSelection: (payload: AmbulanceSelectionPayload) => void
	/** Called when the user confirms their product choice in step 2; advances to step 3. */
	confirmProductSelection: (products: CartProduct[]) => void
	/** Employee slug from URL preset (e.g. team detail page); used to pre-filter templates in step 2. */
	presetEmployeeSlug: string | null
	/** When true, step 2 shows all templates instead of filtering by {@link presetEmployeeSlug}. */
	showAllEmployees: boolean
	/** Clears the employee template filter and shows all doctors in step 2. */
	clearEmployeeFilter: () => void
	/**
	 * Set only on the patient-info step for `TEMPLATE` cart lines so totals match
	 * `calculateDoctorAmbulancePrice`. Cleared on unmount / when not applicable.
	 */
	templateCartPricing: TemplateCartPricingSnapshot | null
	setTemplateCartPricing: (value: SetStateAction<TemplateCartPricingSnapshot | null>) => void
}

const OrderFlowContext = createContext<OrderFlowContextValue | undefined>(undefined)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const VALID_STEPS = new Set<number>(Object.values(ORDER_FLOW_STEPS))

function isValidStep(value: unknown): value is OrderFlowStep {
	return typeof value === 'number' && VALID_STEPS.has(value)
}

function computeInitialStep(
	initialCart: CartProduct[] | undefined,
	initialAmbulanceSlug: string | null | undefined
): OrderFlowStep {
	if (initialCart && initialCart.length > 0) return ORDER_FLOW_STEPS.PERSONAL_DETAILS
	if (initialAmbulanceSlug) return ORDER_FLOW_STEPS.SELECT_PRODUCT
	return ORDER_FLOW_STEPS.SELECT_AMBULANCE
}

/**
 * Reload hard-reset runs in `useLayoutEffect`; React Strict Mode invokes layout effects twice on the
 * same document — skip the second run so we do not double-reset state.
 */
let orderFlowHardResetAfterReloadRan = false

/** Persisted so a later full reload can `history.go(-n)` and drop stale funnel pushes. */
const ORDER_FLOW_SPA_PUSH_DEPTH_STORAGE_KEY = 'medante-order-flow-spa-push-depth-for-reload'

function persistOrderFlowSpaPushDepth(depth: number) {
	try {
		sessionStorage.setItem(ORDER_FLOW_SPA_PUSH_DEPTH_STORAGE_KEY, String(depth))
	} catch {
		// quota / private mode
	}
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type OrderFlowProviderProps = {
	children: ReactNode
	initialFlowMode?: OrderFlowMode
	initialAmbulanceSlug?: string | null
	initialEmployeeSlug?: string | null
	initialCart?: CartProduct[]
}

/**
 * Provides shared order-flow state (current step, selected ambulance, cart)
 * and syncs **session history** in standard mode (`pushState` / `replaceState` with **unchanged URL**);
 * step + visited max live in `history.state` and React state only. A full **reload** resets the funnel
 * to step 1, unwinds prior funnel `pushState` depth when known, and ignores stale history until ambulance
 * is confirmed again.
 */
export function OrderFlowProvider({
	children,
	initialFlowMode = OrderFlowMode.STANDARD,
	initialAmbulanceSlug,
	initialEmployeeSlug,
	initialCart
}: OrderFlowProviderProps) {
	const [flowMode] = useState<OrderFlowMode>(initialFlowMode)
	const [presetEmployeeSlug] = useState<string | null>(initialEmployeeSlug ?? null)
	const [showAllEmployees, setShowAllEmployees] = useState(false)
	const [selectedAmbulanceSlug, setSelectedAmbulanceSlugState] = useState<string | null>(
		initialAmbulanceSlug ?? null
	)
	const setSelectedAmbulanceSlug = useCallback((slug: string | null) => {
		setSelectedAmbulanceSlugState(slug)
		if (slug) saveGa4LastAmbulanceSlug(slug)
	}, [])
	const [cartProducts, setCartProducts] = useState<CartProduct[]>(initialCart ?? [])
	const [currentStep, setCurrentStep] = useState<OrderFlowStep>(
		computeInitialStep(initialCart, initialAmbulanceSlug)
	)
	const currentStepRef = useRef<OrderFlowStep>(currentStep)
	currentStepRef.current = currentStep
	const [maxStepReached, setMaxStepReached] = useState<OrderFlowStep>(
		computeInitialStep(initialCart, initialAmbulanceSlug)
	)
	const [templateCartPricing, setTemplateCartPricing] =
		useState<TemplateCartPricingSnapshot | null>(null)
	const [orderFlowTopLevelCategoryId, setOrderFlowTopLevelCategoryId] = useState<
		string | number | null
	>(null)
	const [orderFlowSectionTheme, setOrderFlowSectionTheme] = useState<SECTION_THEME_ENUM | null>(
		null
	)
	const [ambulancePriceListTableType, setAmbulancePriceListTableType] =
		useState<AMBULANCE_PRICE_LIST_TABLE_TYPE | null>(null)

	// Advance maxStepReached whenever the user reaches a new step.
	useEffect(() => {
		setMaxStepReached(m => (currentStep > m ? currentStep : m))
	}, [currentStep])

	// ---------------------------------------------------------------------------
	// Browser history sync (standard flow; URL unchanged — state in `history.state` + React only)
	// ---------------------------------------------------------------------------

	// `replace` — first paint / guard fixes; `push` — forward confirm (new session segment);
	// `none` — skip (handled `popstate` or about to `history.go`).
	const historyActionRef = useRef<'replace' | 'push' | 'none'>('replace')
	/** Net SPA segments pushed for this funnel (increment on `pushState`; `popstate` applies step delta). */
	const orderFlowSpaPushDepthRef = useRef(0)
	const forwardGoFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	/** Last `orderStep` written by our sync effect; avoids double-`push` (e.g. React Strict dev remount). */
	const lastHistoryWrittenOrderStepRef = useRef<OrderFlowStep | null>(null)
	/**
	 * After a full reload we reset the funnel to step 1 in-memory; session `history` may still contain
	 * older same-URL entries. While this is true, `popstate` coerces any `orderStep` ≠ 1 back to step 1
	 * until the user confirms ambulance again (avoids “forward to step 3” restoring a dead session).
	 */
	const suppressStaleOrderHistoryUntilProgressRef = useRef(false)

	// Full document reload: reset funnel + merge step 1 into current `history.state` (no second navigation).
	useLayoutEffect(() => {
		if (flowMode === OrderFlowMode.DIRECT) return

		const navEntries = performance.getEntriesByType(
			'navigation'
		) as PerformanceNavigationTiming[]
		const nav = navEntries.length > 0 ? navEntries[navEntries.length - 1] : undefined

		if (nav?.type !== 'reload') {
			try {
				sessionStorage.removeItem(ORDER_FLOW_SPA_PUSH_DEPTH_STORAGE_KEY)
			} catch {
				// ignore
			}
			return
		}

		if (orderFlowHardResetAfterReloadRan) return
		orderFlowHardResetAfterReloadRan = true

		let unwind = 0
		try {
			const raw = sessionStorage.getItem(ORDER_FLOW_SPA_PUSH_DEPTH_STORAGE_KEY)
			sessionStorage.removeItem(ORDER_FLOW_SPA_PUSH_DEPTH_STORAGE_KEY)
			if (raw != null && raw !== '') {
				const parsed = parseInt(raw, 10)
				unwind = Number.isFinite(parsed) ? Math.min(Math.max(0, parsed), 25) : 0
			}
		} catch {
			unwind = 0
		}

		// Full reload with `?ambulance=` preset: SSR/hydration already has the correct step + slug.
		// Do not run the generic post-reload reset — it would wipe the URL preset (see order-flow hard reset).
		if (initialAmbulanceSlug) {
			saveGa4LastAmbulanceSlug(initialAmbulanceSlug)
			suppressStaleOrderHistoryUntilProgressRef.current = false
			historyActionRef.current = 'replace'
			orderFlowSpaPushDepthRef.current = 0
			lastHistoryWrittenOrderStepRef.current = null
			const initialStep = computeInitialStep(initialCart, initialAmbulanceSlug)
			currentStepRef.current = initialStep
			setCurrentStep(initialStep)
			setMaxStepReached(initialStep)

			window.history.replaceState(
				{
					...window.history.state,
					orderStep: initialStep,
					orderVisitedMax: initialStep
				},
				''
			)

			// Stale `unwind` from a prior funnel session must not run `history.go(-n)` here: that would
			// navigate away from this fresh `?ambulance=` load (user sees an immediate "bounce back").

			return
		}

		suppressStaleOrderHistoryUntilProgressRef.current = true
		historyActionRef.current = 'replace'
		orderFlowSpaPushDepthRef.current = 0
		lastHistoryWrittenOrderStepRef.current = null
		currentStepRef.current = ORDER_FLOW_STEPS.SELECT_AMBULANCE

		setSelectedAmbulanceSlug(null)
		setCartProducts([])
		setTemplateCartPricing(null)
		setOrderFlowTopLevelCategoryId(null)
		setOrderFlowSectionTheme(null)
		setAmbulancePriceListTableType(null)
		setCurrentStep(ORDER_FLOW_STEPS.SELECT_AMBULANCE)
		setMaxStepReached(ORDER_FLOW_STEPS.SELECT_AMBULANCE)

		window.history.replaceState(
			{
				...window.history.state,
				orderStep: ORDER_FLOW_STEPS.SELECT_AMBULANCE,
				orderVisitedMax: ORDER_FLOW_STEPS.SELECT_AMBULANCE
			},
			''
		)

		// Drop pre-refresh funnel `pushState` segments so the first OS “back” leaves order, not an older entry.
		if (unwind > 0) {
			historyActionRef.current = 'none'
			queueMicrotask(() => {
				window.history.go(-unwind)
			})
		}
	}, [flowMode, initialAmbulanceSlug, initialCart, setSelectedAmbulanceSlug])

	// Whenever currentStep changes, merge funnel markers into `history.state` only (standard flow).
	// In DIRECT mode there is only one step, so we skip all history writes to avoid
	// creating a phantom extra entry that would require an extra back-press to escape.
	//
	// `maxStepReached` is intentionally NOT a dependency: it updates in a separate effect after
	// `currentStep`, which would otherwise run this effect twice for one navigation and double-`push`.
	useEffect(() => {
		if (flowMode === OrderFlowMode.DIRECT) return

		const action = historyActionRef.current
		const orderVisitedMax = Math.max(maxStepReached, currentStep) as OrderFlowStep
		historyActionRef.current = 'push'

		if (action === 'none') {
			return
		}

		if (action === 'push' && lastHistoryWrittenOrderStepRef.current === currentStep) {
			return
		}

		const state = {
			...window.history.state,
			orderStep: currentStep,
			orderVisitedMax
		}

		if (action === 'replace') {
			window.history.replaceState(state, '')
		} else {
			window.history.pushState(state, '')
			orderFlowSpaPushDepthRef.current += 1
			persistOrderFlowSpaPushDepth(orderFlowSpaPushDepthRef.current)
		}
		lastHistoryWrittenOrderStepRef.current = currentStep
		// eslint-disable-next-line react-hooks/exhaustive-deps -- maxStepReached updates after currentStep in another effect; including it double-pushes.
	}, [currentStep, flowMode])

	// Listen for the browser back/forward buttons and drive currentStep from history state.
	// Not needed in DIRECT mode — there are no steps to navigate between.
	useEffect(() => {
		if (flowMode === OrderFlowMode.DIRECT) return

		const handlePopState = (event: PopStateEvent) => {
			if (forwardGoFallbackTimerRef.current != null) {
				clearTimeout(forwardGoFallbackTimerRef.current)
				forwardGoFallbackTimerRef.current = null
			}

			const raw = event.state?.orderStep
			if (!isValidStep(raw)) {
				return
			}

			let targetStep: OrderFlowStep = raw

			if (
				suppressStaleOrderHistoryUntilProgressRef.current &&
				flowMode === OrderFlowMode.STANDARD &&
				raw !== ORDER_FLOW_STEPS.SELECT_AMBULANCE
			) {
				targetStep = ORDER_FLOW_STEPS.SELECT_AMBULANCE
			}

			// Guard: can't be on step 2+ without ambulance in standard flow
			if (
				targetStep >= ORDER_FLOW_STEPS.SELECT_PRODUCT &&
				!selectedAmbulanceSlug &&
				flowMode === OrderFlowMode.STANDARD
			) {
				targetStep = ORDER_FLOW_STEPS.SELECT_AMBULANCE
			}

			// Guard: can't be on step 3 without cart items
			if (targetStep >= ORDER_FLOW_STEPS.PERSONAL_DETAILS && cartProducts.length === 0) {
				targetStep = ORDER_FLOW_STEPS.SELECT_PRODUCT
			}

			// If we had to redirect away from the stored step, replace the now-invalid
			// history entry so the user doesn't get stuck in a back/forward loop.
			historyActionRef.current = targetStep !== raw ? 'replace' : 'none'

			orderFlowSpaPushDepthRef.current = Math.max(
				0,
				orderFlowSpaPushDepthRef.current + (targetStep - currentStepRef.current)
			)
			persistOrderFlowSpaPushDepth(orderFlowSpaPushDepthRef.current)
			lastHistoryWrittenOrderStepRef.current = targetStep

			setCurrentStep(targetStep)
		}

		window.addEventListener('popstate', handlePopState)
		return () => window.removeEventListener('popstate', handlePopState)
	}, [selectedAmbulanceSlug, cartProducts.length, flowMode])

	useEffect(() => {
		return () => {
			if (forwardGoFallbackTimerRef.current != null) {
				clearTimeout(forwardGoFallbackTimerRef.current)
			}
		}
	}, [])

	// ---------------------------------------------------------------------------
	// Actions
	// ---------------------------------------------------------------------------

	const goToStep = useCallback(
		(step: OrderFlowStep, options?: GoToStepOptions) => {
			if (
				step >= ORDER_FLOW_STEPS.SELECT_PRODUCT &&
				!selectedAmbulanceSlug &&
				flowMode === OrderFlowMode.STANDARD
			) {
				return
			}
			if (step >= ORDER_FLOW_STEPS.PERSONAL_DETAILS && cartProducts.length === 0) {
				return
			}
			if (step > maxStepReached) {
				return
			}
			if (step === currentStep) {
				return
			}

			if (flowMode === OrderFlowMode.DIRECT) {
				setCurrentStep(step)
				return
			}

			if (options?.history === 'replace') {
				historyActionRef.current = 'replace'
				setCurrentStep(step)
				return
			}

			if (step < currentStep) {
				const need = currentStep - step
				if (orderFlowSpaPushDepthRef.current < need) {
					historyActionRef.current = 'replace'
					setCurrentStep(step)
					return
				}
				if (forwardGoFallbackTimerRef.current != null) {
					clearTimeout(forwardGoFallbackTimerRef.current)
					forwardGoFallbackTimerRef.current = null
				}
				historyActionRef.current = 'none'
				window.history.go(step - currentStep)
				return
			}

			if (step > currentStep) {
				const delta = step - currentStep
				if (forwardGoFallbackTimerRef.current != null) {
					clearTimeout(forwardGoFallbackTimerRef.current)
				}
				historyActionRef.current = 'none'
				window.history.go(delta)
				const target = step
				forwardGoFallbackTimerRef.current = setTimeout(() => {
					forwardGoFallbackTimerRef.current = null
					if (currentStepRef.current !== target) {
						historyActionRef.current = 'push'
						setCurrentStep(target)
					}
				}, 20)
				return
			}

			setCurrentStep(step)
		},
		[currentStep, maxStepReached, selectedAmbulanceSlug, cartProducts.length, flowMode]
	)

	const confirmAmbulanceSelection = useCallback(
		(payload: AmbulanceSelectionPayload) => {
			suppressStaleOrderHistoryUntilProgressRef.current = false
			setTemplateCartPricing(null)
			setCartProducts([])
			setShowAllEmployees(false)
			setSelectedAmbulanceSlug(payload.ambulance.slug)
			saveGa4LastAmbulanceSlug(payload.ambulance.slug, payload.ambulance.name)
			setOrderFlowTopLevelCategoryId(payload.topLevelCategoryId)
			setOrderFlowSectionTheme(payload.sectionTheme)
			setAmbulancePriceListTableType(payload.ambulance.priceListTableType)
			setCurrentStep(ORDER_FLOW_STEPS.SELECT_PRODUCT)
		},
		[setSelectedAmbulanceSlug]
	)

	const clearEmployeeFilter = useCallback(() => {
		setShowAllEmployees(true)
	}, [])

	const confirmProductSelection = useCallback((products: CartProduct[]) => {
		setTemplateCartPricing(null)
		setCartProducts(products)
		setCurrentStep(ORDER_FLOW_STEPS.PERSONAL_DETAILS)
	}, [])

	// ---------------------------------------------------------------------------
	// Context value
	// ---------------------------------------------------------------------------

	const value = useMemo<OrderFlowContextValue>(
		() => ({
			flowMode,
			selectedAmbulanceSlug,
			setSelectedAmbulanceSlug,
			orderFlowTopLevelCategoryId,
			orderFlowSectionTheme,
			ambulancePriceListTableType,
			currentStep,
			maxStepReached,
			cartProducts,
			goToStep,
			confirmAmbulanceSelection,
			confirmProductSelection,
			presetEmployeeSlug,
			showAllEmployees,
			clearEmployeeFilter,
			templateCartPricing,
			setTemplateCartPricing
		}),
		[
			flowMode,
			selectedAmbulanceSlug,
			setSelectedAmbulanceSlug,
			orderFlowTopLevelCategoryId,
			orderFlowSectionTheme,
			ambulancePriceListTableType,
			currentStep,
			maxStepReached,
			cartProducts,
			goToStep,
			confirmAmbulanceSelection,
			confirmProductSelection,
			presetEmployeeSlug,
			showAllEmployees,
			clearEmployeeFilter,
			templateCartPricing
		]
	)

	return <OrderFlowContext.Provider value={value}>{children}</OrderFlowContext.Provider>
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Consume order-flow state. Must be used inside `<OrderFlowProvider>`. */
export function useOrderFlow() {
	const ctx = useContext(OrderFlowContext)
	if (ctx === undefined) {
		throw new Error('useOrderFlow must be used within OrderFlowProvider')
	}
	return ctx
}
